import {useFormik} from 'formik'
import * as Yup from 'yup'
import {Col, Form, Row, Toast, ToastContainer} from 'react-bootstrap'
import {useEffect, useRef, useState} from 'react'
import Select from 'react-select'
import {
  offerListForSeller,
  locationListApi,
  getProductGroup,
  getGradeGroup,
  getProducts,
} from './_request'
import {showToast, useDebounce} from '../../../_metronic/helpers'
import download from 'downloadjs'
import {ToastType} from '../../../_metronic/helpers/crud-helper/ToastTypes'
import {checkPermission} from '../../utils/checkPermission'

const SellerMessage = () => {
  const [sessionMessage, setSessionMessage] = useState({
    type: '',
    message: '',
    show: false,
  })
  const [loading, setLoading] = useState(false)
  const [productGroupList, setProductGroupList] = useState<any>([])
  const [gradeGroupList, setGradeGroupList] = useState<any>([])
  const [selectedProductGroupList, setSelectedProductGroupList] = useState<any>({})
  const [selectedGradeGroupList, setSelectedGradeGroupList] = useState<any>({})
  const [productList, setProductList] = useState<any>([])
  const [selectedProductList, setSelectedProductList] = useState<any>({})
  const [locationList, setLocationList] = useState<any>([])
  const [selectedStatusList, setSelectedStatusList] = useState<any>({
    label: 'Active',
    value: '1',
  })
  const [statusList, setStatusList] = useState<any>([])
  const [selectedLocationList, setSelectedLocationList] = useState<any>({})
  const [offerDetail, setOfferDetail] = useState<any>([])
  const [offerPrice, setOfferPrice] = useState<any>([])
  const [getCityData, setCityData] = useState<any>()

  // Raw search text as the user types in each Select's search box.
  const [gradeGroupSearch, setGradeGroupSearch] = useState('')
  const [productGroupSearch, setProductGroupSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [locationSearch, setLocationSearch] = useState('')

  // Debounced so we don't fire an API call on every keystroke.
  const debouncedGradeGroupSearch = useDebounce(gradeGroupSearch, 300)
  const debouncedProductGroupSearch = useDebounce(productGroupSearch, 300)
  const debouncedProductSearch = useDebounce(productSearch, 300)
  const debouncedLocationSearch = useDebounce(locationSearch, 300)

  const productGroupRequestIdRef = useRef(0)
  const productRequestIdRef = useRef(0)

  const offerValidation = Yup.object()
    .shape({
      product_group_id: Yup.string(),
      grade_group_id: Yup.string(),
      product_id: Yup.string(),
      destination_location_id: Yup.string().required('Location is required'),
      status_id: Yup.string().required('Status is required'),
      quantity: Yup.number()
        .typeError('Quantity must be a number')
        .required('Quantity is required')
        .min(1, 'Quantity must be between 1 and 100 MT')
        .max(100, 'Quantity must be between 1 and 100 MT'),
      radius_km: Yup.number()
        .typeError('Radius is required')
        .required('Radius is required')
        .min(50, 'Radius must be between 50 and 5000 KM')
        .max(5000, 'Radius must be between 50 and 5000 KM'),
    })
    .test(
      'at-least-one-product-filter',
      'Select at least one of Grade Group, Product Group, or Product',
      function (values) {
        const {grade_group_id, product_group_id, product_id} = values || {}
        if (grade_group_id || product_group_id || product_id) {
          return true
        }
        // Attach the error to grade_group_id so it renders under that field.
        return this.createError({
          path: 'grade_group_id',
          message: 'Select at least one of Grade Group, Product Group, or Product',
        })
      }
    )

  const formik = useFormik({
    initialValues: {
      product_group_id: '',
      grade_group_id: '',
      product_id: '',
      status_id: '1',
      destination_location_id: '',
      quantity: '30',
      radius_km: '50',
    },
    validationSchema: offerValidation,
    onSubmit: async (values: any) => {
      // Kept for backward compatibility with the original submit flow.
      // Primary actions are now triggered via the Push Notification /
      // Download Excel buttons below (handlePushNotificationClick /
      // handleDownloadExcelClick), which call offerListForSeller directly
      // with the appropriate `action`.
    },
  })

  useEffect(() => {
    if (sessionMessage.show) {
      setTimeout(() => {
        setSessionMessage({
          type: '',
          message: '',
          show: false,
        })
      }, 5000)
    }
  }, [sessionMessage.message])

  // gradeGroupIdOverride lets callers (e.g. Grade Group onChange) pass the
  // *new* grade_group_id explicitly, since formik.values won't reflect the
  // just-set value synchronously in the same tick.
  const getProductGroupList = async (search: any, gradeGroupIdOverride?: any) => {
    const requestId = ++productGroupRequestIdRef.current
    try {
      const gradeGroupId =
        gradeGroupIdOverride !== undefined
          ? gradeGroupIdOverride
          : formik.values.grade_group_id && formik.values.grade_group_id !== 'all'
          ? formik.values.grade_group_id
          : undefined

      let {responseStatus, responseMessage, data} = (await getProductGroup(
        search,
        gradeGroupId
      )) as any

      // A newer request has since been fired — ignore this stale result.
      if (requestId !== productGroupRequestIdRef.current) return

      if (responseStatus == 200 && data) {
        let updatedProductGroupList = data.map((x: any) => {
          x.label = x.name
          x.value = x.id
          return x
        })
        setProductGroupList(updatedProductGroupList)
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({type: 'error', message: message, show: true})
      }
    } catch (err: any) {
      if (requestId !== productGroupRequestIdRef.current) return
      let message = err?.message
        ? err.message
        : err?.responseMessage
        ? err.responseMessage
        : ('Something went wrong' as any)
      setSessionMessage({type: 'error', message: message, show: true})
    }
  }

  // productGroupIdOverride: same pattern, used by Product Group onChange.
  const getProductList = async (search: any, productGroupIdOverride?: any) => {
    const requestId = ++productRequestIdRef.current
    try {
      const productGroupId =
        productGroupIdOverride !== undefined
          ? productGroupIdOverride
          : formik.values.product_group_id && formik.values.product_group_id !== 'all'
          ? formik.values.product_group_id
          : undefined

      let {responseStatus, responseMessage, data} = (await getProducts(
        search,
        productGroupId
      )) as any

      if (requestId !== productRequestIdRef.current) return

      if (responseStatus == 200 && data) {
        let updatedProductList = data.map((x: any) => {
          x.label = x.product_variation
          x.value = x.id
          return x
        })
        setProductList(updatedProductList)
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({type: 'error', message: message, show: true})
      }
    } catch (err: any) {
      if (requestId !== productRequestIdRef.current) return
      let message = err?.message
        ? err.message
        : err?.responseMessage
        ? err.responseMessage
        : ('Something went wrong' as any)
      setSessionMessage({type: 'error', message: message, show: true})
    }
  }
  const getGradeGroupList = async (search: any) => {
    try {
      let {responseStatus, responseMessage, data} = (await getGradeGroup(search)) as any
      if (responseStatus == 200 && data) {
        let updatedGradeGroupList = data.map((x: any) => {
          x.label = x.name
          x.value = x.id
          return x
        })
        setGradeGroupList(updatedGradeGroupList)
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({type: 'error', message: message, show: true})
      }
    } catch (err: any) {
      let message = err?.message
        ? err.message
        : err?.responseMessage
        ? err.responseMessage
        : ('Something went wrong' as any)
      setSessionMessage({type: 'error', message: message, show: true})
    }
  }

  // Populates the static status options — call ONCE (e.g. in the mount useEffect).
  const initStatusList = () => {
    const statusListData = [
      {label: 'All', value: 'all'},
      {label: 'Active', value: '1'},
      {label: 'Inactive', value: '0'},
    ]
    setStatusList(statusListData)
  }

  // Pure read of the current selection — safe to call from render / `value`.
  const getSelectedStatusList = () => {
    if (formik.values.status_id === 'all') {
      return [{label: 'All', value: 'all'}]
    }
    if (!Array.isArray(statusList)) {
      return []
    }
    const found = statusList.find((x: any) => x.value === formik.values.status_id)
    return found ? [found] : []
  }

  const getLocationList = async (search: any) => {
    try {
      let {responseStatus, responseData, responseMessage} = (await locationListApi(search)) as any
      if (responseStatus == 200 && responseData) {
        setLocationList(
          responseData.map((x: any) => {
            x.label = x.name
            x.value = x.id
            setCityData(x?.name)
            return x
          })
        )
      } else {
        let message = responseMessage ? responseMessage : 'Something went wrong'
        setSessionMessage({type: 'error', message: message, show: true})
      }
    } catch (err: any) {
      let message = err?.message
        ? err.message
        : err?.responseMessage
        ? err.responseMessage
        : ('Something went wrong' as any)
      setSessionMessage({type: 'error', message: message, show: true})
    }
  }

  // Returns [] (no selection) so the placeholder shows until the user picks an option.
  const getSelectedProductGroupList = () => {
    if (!formik.values.product_group_id) {
      return []
    }
    if (selectedProductGroupList.value == formik.values.product_group_id) {
      if (!productGroupList.find((x: any) => x.value == formik.values.product_group_id)) {
        productGroupList.push(selectedProductGroupList)
        setProductGroupList(productGroupList)
      }
      return [selectedProductGroupList]
    } else return []
  }

  // Returns [] (no selection) so the placeholder shows until the user picks an option.
  const getSelectedProductList = () => {
    if (!formik.values.product_id) {
      return []
    }
    if (selectedProductList.value == formik.values.product_id) {
      if (!productList.find((x: any) => x.value == formik.values.product_id)) {
        productList.push(selectedProductList)
        setProductList(productList)
      }
      return [selectedProductList]
    } else return []
  }

  // Returns [] (no selection) so the placeholder shows until the user picks an option.
  const getSelectedGradeGroupList = () => {
    if (!formik.values.grade_group_id) {
      return []
    }
    if (selectedGradeGroupList.value == formik.values.grade_group_id) {
      if (!gradeGroupList.find((x: any) => x.value == formik.values.grade_group_id)) {
        gradeGroupList.push(selectedGradeGroupList)
        setGradeGroupList(gradeGroupList)
      }
      return [selectedGradeGroupList]
    } else return []
  }

  const getSelectedLocationList = () => {
    if (selectedLocationList.value == formik.values.destination_location_id) {
      if (!locationList.find((x: any) => x.value == formik.values.destination_location_id)) {
        locationList.push(selectedLocationList)
        setLocationList(locationList)
      }
      return [selectedLocationList]
    } else return []
  }

  // Validates the fields required for the Seller Message Module actions
  // (Push Notification / Download Excel) and marks them touched so errors
  // render. `quantity` is required for both actions.
  const validateSellerMessageFields = async () => {
    const errors = await formik.validateForm()

    formik.setTouched({
      grade_group_id: true,
      product_group_id: true,
      product_id: true,
      destination_location_id: true,
      status_id: true,
      radius_km: true,
      quantity: true,
    })

    const relevantKeys = [
      'grade_group_id',
      'product_group_id',
      'product_id',
      'destination_location_id',
      'status_id',
      'radius_km',
      'quantity',
    ]

    const hasBlockingError = relevantKeys.some((key) => (errors as any)[key])
    return !hasBlockingError
  }

  const handlePushNotificationClick = async () => {
    const isValid = await validateSellerMessageFields()
    if (!isValid) return

    setLoading(true)
    try {
      const res: any = await offerListForSeller({
        grade_group_id: formik.values.grade_group_id,
        product_group_id: formik.values.product_group_id,
        product_id: formik.values.product_id,
        buyer_destination_location_id: formik.values.destination_location_id,
        offer_status: formik.values.status_id,
        quantity: formik.values.quantity,
        radius_km: formik.values.radius_km,
        action: 'push_notification',
      })

      const {responseStatus, responseMessage, responseData} = res?.data || {}

      if (responseStatus === 200) {
        showToast({
          message: `Notified ${responseData?.notified_seller_count ?? 0} seller(s), ${
            responseData?.device_token_count ?? 0
          } device(s) reached.`,
          type: ToastType.SUCCESS,
          autoClose: 2000,
        })
      } else {
        showToast({
          message: responseMessage || 'Something went wrong',
          type: ToastType.WARNING,
          autoClose: 2000,
        })
      }
    } catch (err: any) {
      const message = err?.response?.data?.responseMessage || err?.message || 'Something went wrong'
      showToast({message, type: ToastType.WARNING, autoClose: 2000})
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadExcelClick = async () => {
    const isValid = await validateSellerMessageFields()
    if (!isValid) return

    setLoading(true)
    try {
      const res: any = await offerListForSeller({
        grade_group_id: formik.values.grade_group_id,
        product_group_id: formik.values.product_group_id,
        product_id: formik.values.product_id,
        buyer_destination_location_id: formik.values.destination_location_id,
        offer_status: formik.values.status_id,
        quantity: formik.values.quantity,
        radius_km: formik.values.radius_km,
        action: 'excel',
      })

      const content = res?.headers?.['content-type'] || ''

      if (content.includes('application/json')) {
        // Backend fell back to a JSON error response even though we asked
        // for a blob (e.g. "No sellers with valid contact numbers found").
        const text = await res.data.text()
        const jsonResponse = JSON.parse(text)
        showToast({
          message: jsonResponse.responseMessage || 'No data found for export',
          type: ToastType.WARNING,
          autoClose: 2000,
        })
        return
      }

      download(res.data, 'Seller Message Export.csv', content)
      showToast({
        message: 'Export downloaded successfully',
        type: ToastType.SUCCESS,
        autoClose: 1000,
      })
    } catch (err: any) {
      const message = err?.response?.data?.responseMessage || err?.message || 'Something went wrong'
      showToast({message, type: ToastType.WARNING, autoClose: 2000})
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    formik.resetForm()

    setSelectedProductGroupList({})
    setSelectedGradeGroupList({})
    setSelectedProductList({})
    setSelectedStatusList({label: 'Active', value: '1'})
    setSelectedLocationList({})

    setOfferDetail([])
    setOfferPrice([])
    setSessionMessage({type: '', message: '', show: false})

    // Grade Group cleared -> Product Group and Product lists should go back
    // to their full, unfiltered lists. Pass '' (not undefined) as the
    // override: formik.values hasn't reflected resetForm() yet in this same
    // tick, so undefined would fall back to the *stale* (pre-reset) grade
    // group / product group id instead of clearing the filter.
    getProductGroupList('', '')
    getProductList('', '')
  }

  useEffect(() => {
    initStatusList()
  }, [])

  // Debounced search effects — fire once on mount (initial debounced value is
  // '') and again only after the user stops typing for 300ms. Cascade-driven
  // refetches (grade group -> product group -> product, and reset) call the
  // list functions directly instead, since those are a single deliberate
  // action rather than typing.
  useEffect(() => {
    getGradeGroupList(debouncedGradeGroupSearch)
  }, [debouncedGradeGroupSearch])

  useEffect(() => {
    getProductGroupList(debouncedProductGroupSearch)
  }, [debouncedProductGroupSearch])

  useEffect(() => {
    getProductList(debouncedProductSearch)
  }, [debouncedProductSearch])

  useEffect(() => {
    getLocationList(debouncedLocationSearch)
  }, [debouncedLocationSearch])

  return (
    <>
      {checkPermission('Seller Messaging Module', 'list') ? (
        <>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='fw-bold fs-2'>Seller Messaging Module</div>
          </div>
          <div className='card card-body p-12 mt-10'>
            <Row className='mb-5'>
              <Col md={1}></Col>
              <Col md={10}>
                <form
                  className='form w-100'
                  onSubmit={formik.handleSubmit}
                  noValidate
                  id='kt_login_signin_form'
                >
                  <Row className='mb-15'>
                    <Col xl={4}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Grade Group</Form.Label>
                        <Select
                          options={gradeGroupList}
                          className='custom_select'
                          classNamePrefix='Select'
                          placeholder='Select Grade Group'
                          name='grade_group_id'
                          onInputChange={(value: string, actionMeta: any) => {
                            // react-select also fires onInputChange on blur /
                            // menu-close / selection — only real typing
                            // should trigger a search.
                            if (actionMeta.action !== 'input-change') return
                            setGradeGroupSearch(value)
                          }}
                          value={getSelectedGradeGroupList()}
                          onChange={(e: any) => {
                            formik.setFieldValue('grade_group_id', e.value)
                            setSelectedGradeGroupList(e)

                            // Cascade reset: clear Product Group and Product,
                            // then refetch Product Group list scoped to the
                            // newly selected Grade Group.
                            formik.setFieldValue('product_group_id', '')
                            formik.setFieldValue('product_id', '')
                            setSelectedProductGroupList({})
                            setSelectedProductList({})
                            setProductList([])
                            getProductGroupList('', e.value)
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xl={4}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>
                          Product Group
                        </Form.Label>
                        <Select
                          options={productGroupList}
                          className='custom_select'
                          classNamePrefix='Select'
                          placeholder='Select Product Group'
                          name='product_group_id'
                          onInputChange={(value: string, actionMeta: any) => {
                            if (actionMeta.action !== 'input-change') return
                            setProductGroupSearch(value)
                          }}
                          value={getSelectedProductGroupList()}
                          onChange={(e: any) => {
                            formik.setFieldValue('product_group_id', e.value)
                            setSelectedProductGroupList(e)

                            // Cascade reset: clear Product, then refetch
                            // Product list scoped to the newly selected Product Group.
                            formik.setFieldValue('product_id', '')
                            setSelectedProductList({})
                            getProductList('', e.value)
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xl={4}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Product</Form.Label>
                        <Select
                          options={productList}
                          className='custom_select'
                          classNamePrefix='Select'
                          placeholder='Select Product'
                          name='product_id'
                          onInputChange={(value: string, actionMeta: any) => {
                            if (actionMeta.action !== 'input-change') return
                            setProductSearch(value)
                          }}
                          value={getSelectedProductList()}
                          onChange={(e: any) => {
                            formik.setFieldValue('product_id', e.value)
                            setSelectedProductList(e)
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xl={12}>
                      {/* Guard on the live values too, not just formik.errors: the cascading
                          onChange handlers below fire several setFieldValue calls in the same
                          tick, and Formik's async validateOnChange can resolve out of order,
                          leaving a stale error even after a valid selection is made. */}
                      {formik?.touched?.grade_group_id &&
                        formik?.errors?.grade_group_id &&
                        !formik.values.grade_group_id &&
                        !formik.values.product_group_id &&
                        !formik.values.product_id && (
                          <div className='fv-plugins-message-container'>
                            <span role='alert' className={'text-danger'}>
                              {formik?.errors?.grade_group_id as any}
                            </span>
                          </div>
                        )}
                    </Col>
                  </Row>

                  <Row className='mb-5'>
                    <Col xl={4}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>
                          Buyer Destination Location
                        </Form.Label>
                        <Select
                          options={locationList}
                          className='custom_select'
                          classNamePrefix='Select'
                          placeholder='Select Location'
                          onInputChange={(value: string, actionMeta: any) => {
                            if (actionMeta.action !== 'input-change') return
                            setLocationSearch(value)
                          }}
                          value={getSelectedLocationList()}
                          onChange={(e: any) => {
                            formik.setFieldValue('destination_location_id', e.value)
                            setSelectedLocationList(e)
                          }}
                        />
                        {formik?.touched?.destination_location_id &&
                          formik?.errors?.destination_location_id && (
                            <div className='fv-plugins-message-container'>
                              <span role='alert' className={'text-danger'}>
                                {formik?.errors?.destination_location_id as any}
                              </span>
                            </div>
                          )}
                      </Form.Group>
                    </Col>
                    <Col xl={3}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Status</Form.Label>
                        <Select
                          options={statusList}
                          className='custom_select'
                          classNamePrefix='Select'
                          placeholder='Select Status'
                          value={getSelectedStatusList()}
                          onChange={(e: any) => {
                            formik.setFieldValue('status_id', e.value)
                            setSelectedStatusList(e)
                          }}
                        />
                        {formik?.touched?.status_id && formik?.errors?.status_id && (
                          <div className='fv-plugins-message-container'>
                            <span role='alert' className={'text-danger'}>
                              {formik?.errors?.status_id as any}
                            </span>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col xl={3}>
                      <div className='mb-8'>
                        <Form.Label className='fw-semibold required fs-6 mb-2'>
                          Radius (KM)
                        </Form.Label>

                        <div className='input-group'>
                          <Form.Control
                            type='number'
                            min={50}
                            max={5000}
                            placeholder='Enter radius in KM'
                            name='radius_km'
                            value={formik.values.radius_km}
                            onChange={(e: any) => {
                              formik.setFieldValue('radius_km', e.target.value.trimStart())
                            }}
                            onBlur={formik.handleBlur}
                            className='form-control-solid'
                            onWheel={(e) => e.currentTarget.blur()}
                          />

                          <span className='input-group-text'>KM</span>
                        </div>

                        <div className='text-muted fs-7 mt-2'>
                          Minimum: 50 KM | Maximum: 5000 KM
                        </div>

                        {formik.touched.radius_km && formik.errors.radius_km && (
                          <div className='fv-plugins-message-container'>
                            <span role='alert' className={'text-danger'}>
                              {formik?.errors?.radius_km as any}
                            </span>
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col xl={2}>
                      <Form.Group>
                        <Form.Label className='fw-bold required form-label'>Quantity</Form.Label>
                        <Form.Control
                          type='text'
                          inputMode='decimal'
                          className='form-control-solid'
                          placeholder='Enter Qty'
                          value={formik.values.quantity}
                          onChange={(e: any) => {
                            // Allow only 1 to 100 with up to 3 decimal places (e.g. 12.365)
                            const value = e.target.value.trim()
                            if (/^\d{0,3}(\.\d{0,3})?$/.test(value) && (value === '' || Number(value) <= 100)) {
                              formik.setFieldValue('quantity', value)
                            }
                          }}
                        />
                        {formik.touched.quantity && formik.errors.quantity && (
                          <div className='fv-plugins-message-container'>
                            <span role='alert' className={'text-danger'}>
                              {formik?.errors?.quantity as any}
                            </span>
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    {/* Action Buttons */}
                    <Col className='mt-5'>
                      <div className='d-flex flex-column flex-sm-row justify-content-end gap-10'>
                        {/* Reset */}
                        <button
                          type='button'
                          className='btn btn-light px-5 py-2 fw-semibold'
                          onClick={() => {
                            handleReset()
                          }}
                        >
                          Reset
                        </button>

                        {/* Push Notification */}
                        <button
                          type='button'
                          className='btn btn-warning px-5 py-2 fw-semibold text-white d-flex align-items-center justify-content-center'
                          onClick={handlePushNotificationClick}
                          disabled={loading}
                        >
                          <i className='fa-solid fa-bell me-1'></i>
                          {loading ? 'Processing...' : 'Push Notification'}
                        </button>

                        {/* Download Excel */}
                        <button
                          type='button'
                          className='btn btn-success px-5 py-2 fw-semibold d-flex align-items-center justify-content-center'
                          onClick={handleDownloadExcelClick}
                          disabled={loading}
                        >
                          <i className='fa-solid fa-file-excel me-1'></i>
                          {loading ? 'Processing...' : 'Download Excel'}
                        </button>
                      </div>
                    </Col>
                  </Row>

                  <ToastContainer className='p-3' position={'top-end'}>
                    <Toast
                      show={sessionMessage.show}
                      onClose={() => {
                        setSessionMessage({
                          type: '',
                          message: '',
                          show: false,
                        })
                      }}
                      bg='danger'
                    >
                      <Toast.Body className='text-white'>{sessionMessage.message}</Toast.Body>
                    </Toast>
                  </ToastContainer>
                </form>
              </Col>
            </Row>
          </div>
        </>
      ) : (
        ''
      )}
    </>
  )
}
export default SellerMessage
